import { DestroyRef, Injectable, signal, Signal } from '@angular/core';
import { debounceTime, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { Coordinates, Feature } from '@api/models';
import { PeliasService } from '@api/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { translate } from '@ngneat/transloco';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatAutocomplete } from '@angular/material/autocomplete';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  public isMobile = signal(window.innerWidth < 768)

  constructor(private peliasService: PeliasService, private snackBar: MatSnackBar, private clipboard: Clipboard) {}

  public applyRxJSOperatorsToFeatureAutocomplete(form: FormGroup, fieldName: string, currentValue:Signal<Feature|null>, destroyRef: DestroyRef) {

    const field = form.get(fieldName);
    if (!field) {
      return of([])
    }

    return field.valueChanges.pipe(
      takeUntilDestroyed(destroyRef),
      debounceTime(500),
      distinctUntilChanged(),
      tap((text) => {
        if(!currentValue() || text !== currentValue()?.properties.name){
          form.get(fieldName)?.enable();
        }
      }),
      
      filter((text) =>!currentValue() || text !== currentValue()?.properties.name),
      switchMap((text) => {
        if(!text || text.length < 1) { return of({features: [], searchText: ''}) }
        if (isNaN(parseInt(text.charAt(text.length - 1), 10))) {
          return this.peliasService.autocompleteRequest(text as string, 'autocomplete',['venue,street']).pipe(
            map((response) => ({features: response.features, searchText: text}))
          )
        }
        return this.peliasService.autocompleteRequest(text as string).pipe(
          map((response) => ({features: response.features, searchText: text}))
        )
      }),
      switchMap(({features, searchText}) => {
        if (features.length === 0 && searchText.length >= 4) {
          return this.peliasService.autocompleteRequest(searchText as string, 'search').pipe(
            map((response) => response.features),
          )
        } else {
          return of(features);
        }
      })
    );
  }

  public showSnackBarError(error = 'unknown', action = 'map.close', className = 'red-snackbar') {
    this.snackBar.open(translate('errors.' + error), translate(action), {
      duration: 2000,
      panelClass: [className]
    });
  }

  public showSnackBarSuccess(message = 'unknown', action = 'map.close', className = 'green-snackbar') {
    this.snackBar.open(translate(message), translate(action), {
      duration: 2000,
      panelClass: [className]
    });
  }

  public copyToClipboard(text: string) {
    this.clipboard.copy(text);
    this.showSnackBarSuccess('map.copiedOnClipboard')
  }

  public encodeToSafeBase64(text: string) {
        // Encode to base64
        const base64EncodedString = btoa(text);
    
        // Remove invalid characters (not allowed on a URL) from the string
        const urlSafeBase64 = base64EncodedString.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        
        return urlSafeBase64;
  }

  public decodeFromSafeBase64(base64String: string): string {
    // Revert encode
    const base64EncodedString = base64String.replace(/-/g, '+').replace(/_/g, '_');
    
    // Calculate number of '='s to pad the string with
    const padding = 4 - (base64EncodedString.length % 4);
    
    // Add the appropriate number of '='s to the end of the string
    const base64EncodedStringWithPadding = padding === 4 ? base64EncodedString : base64EncodedString + '='.repeat(padding);
    
    // Decode string
    const decodedString = atob(base64EncodedStringWithPadding);
    
    return decodedString;
  }

  public createFeatureFromCoordinates(coordinates: Coordinates, label: string): Feature {
    return {
      type:"Feature",
      geometry: {
        type: 'Point', 
        coordinates: [coordinates.lng, coordinates.lat],
      },
      properties: {name: label as string}
      };
    }

    
  public cleanAutocomplete(auto?: MatAutocomplete) {
    if(!auto) { return; }
    auto.options.forEach((item) => {
      item.deselect(false)
    });
  }
}
